import React, { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { useTheme } from '@zydalabs/storefront-components';
import useTranslation from 'next-translate/useTranslation';
import { Typography, Tooltip } from '@zydalabs/zac-react';
import { VariantProperty, CartItemOptions } from '../../../../service/types/generated';
import { StyledAlignItemsBetween } from '../../../../common/styles';
import MultiAddon from './components/MultiAddon';
import SingleOption from './components/SingleOption';
import MultipleOptions from './components/MultipleOptions';
import { StyledBulkTitleWrap } from './style';

type MenuItemOptionsType = {
  optionGroup: VariantProperty;
  selectedOptions: Array<CartItemOptions>;
  setSelectedOptions: (x: Array<CartItemOptions>) => void;
  showValidation: boolean;
  setHasError: (
    optionGroupsError: Record<string, { hasError: boolean; optionGroupRef: React.RefObject<HTMLDivElement> }>,
  ) => void;
  isArabic: boolean;
  setBulkCount: Dispatch<SetStateAction<number>>;
  maximumBulkQuantity?: number;
  toolTipText?: string;
  stepBy?: number;
  isBulkLimitReached?: boolean;
};

const MenuItemOptionGroup: React.FC<MenuItemOptionsType> = ({
  optionGroup,
  selectedOptions,
  setSelectedOptions,
  showValidation,
  setHasError,
  isArabic,
  maximumBulkQuantity,
  setBulkCount,
  toolTipText,
  stepBy,
  isBulkLimitReached,
}) => {
  const [totalMultiAddonCount, setTotalMultiAddonCount] = useState<number>(0);
  const [error, setError] = useState<boolean>(false);
  const theme: any = useTheme();
  const { t } = useTranslation('product');
  const optionGroupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHasError({ [optionGroup.uuid]: { hasError: error, optionGroupRef } });
  }, [error]);

  useEffect(() => {
    const initialTotalMultiAddonCount =
      selectedOptions
        ?.filter(option => option.optionGroupId === optionGroup.uuid)
        ?.reduce((accumulator, current) => accumulator + (current?.optionQuantity || 0), 0) || 0;

    if (initialTotalMultiAddonCount !== totalMultiAddonCount) setTotalMultiAddonCount(initialTotalMultiAddonCount);
  }, [selectedOptions]);

  const addMultiAddOnOption = (option: CartItemOptions) => {
    const existedMultiAddOnOption = selectedOptions.find(opt => opt.optionId === option.optionId);
    if (existedMultiAddOnOption) {
      existedMultiAddOnOption.optionQuantity = option.optionQuantity;
      setSelectedOptions(selectedOptions.filter(opt => opt.optionQuantity !== 0));
    } else setSelectedOptions([...selectedOptions, option]);
  };

  const filteredOptions = optionGroup?.options?.filter(option => !optionGroup?.excludedPropertyValues?.includes(option.uuid));

  if (filteredOptions.length === 0) {
    return null;
  }

  const addSingleOption = (option: CartItemOptions) => {
    const existedGroupOption = selectedOptions.find(opt => opt.optionGroupId === option.optionGroupId);
    if (existedGroupOption) {
      const modifiedOptions = selectedOptions.map(opt => {
        if (opt.optionId === existedGroupOption.optionId) {
          return { ...existedGroupOption, optionId: option.optionId };
        }
        return opt;
      });
      setSelectedOptions(modifiedOptions);
    } else setSelectedOptions([...selectedOptions, option]);
  };

  const addMultipleOptions = (options: Array<CartItemOptions>, existedOptionId: string) => {
    const filteredMultipleOptions = selectedOptions.filter(opt => opt.optionGroupId !== existedOptionId);
    setSelectedOptions([...filteredMultipleOptions, ...options]);
  };

  const getOptionsHintMessages = (minQuantity, maxQuantity, freeOptionsCount) => {
    const messages = [];
    if (minQuantity === maxQuantity && minQuantity !== 0) {
      messages.push(`${t('select')} ${maxQuantity}`);
    } else if (minQuantity > 0 && maxQuantity) {
      messages.push(`${t('select')} ${t('atLeast')} ${minQuantity} ${t('and')} ${t('upTo')} ${maxQuantity}`);
    } else if (maxQuantity && maxQuantity > 0) {
      messages.push(`${t('select')} ${t('upTo')} ${maxQuantity}`);
    }

    if (freeOptionsCount > 0) {
      messages.push(freeOptionsCount === 1 ? t('oneItemWillBeFree') : `${freeOptionsCount} ${t('itemsWillBeFree')}`);
    }

    return messages;
  };

  const renderOption = () => {
    if (optionGroup.multipleAddOnsEnabled)
      return (
        <>
          {filteredOptions?.map(option => {
            const initialCount =
              selectedOptions?.find(valuedOption => valuedOption.optionId === option.uuid)?.optionQuantity || 0;

            return (
              <MultiAddon
                key={option.uuid}
                isArabic={isArabic}
                existedOptionId={optionGroup.uuid}
                existedOptionMinQuantity={optionGroup.minQuantity}
                option={option}
                disableIncrement={
                  (optionGroup.maxQuantity > 0 && totalMultiAddonCount === optionGroup.maxQuantity) ||
                  (!!optionGroup.maxQuantity && totalMultiAddonCount + stepBy > optionGroup.maxQuantity) ||
                  isBulkLimitReached
                }
                totalMultiAddonCount={totalMultiAddonCount}
                setTotalMultiAddonCount={setTotalMultiAddonCount}
                addMultiAddOnOption={addMultiAddOnOption}
                setError={setError}
                initialCount={initialCount}
                setBulkCount={setBulkCount}
                stepBy={stepBy}
                maximumBulkQuantity={maximumBulkQuantity}
              />
            );
          })}
        </>
      );

    if (optionGroup.minQuantity === 1 && optionGroup.maxQuantity === 1) {
      const selectedOptionId = selectedOptions?.find(valuedOption => valuedOption.optionGroupId === optionGroup.uuid)?.optionId;
      return (
        <div data-testid="single-option-component">
          <SingleOption
            existedOptionId={optionGroup.uuid}
            options={filteredOptions}
            isArabic={isArabic}
            addSingleOption={addSingleOption}
            selectedOptionId={selectedOptionId}
            setError={setError}
          />
        </div>
      );
    }

    const selectedOptionsIds =
      selectedOptions?.reduce((accumulator, current) => {
        if (current.optionGroupId === optionGroup.uuid) accumulator.push(current.optionId);
        return accumulator;
      }, []) || [];

    return (
      <MultipleOptions
        optionGroup={{ ...optionGroup, options: filteredOptions }}
        addMultipleOptions={addMultipleOptions}
        setError={setError}
        isArabic={isArabic}
        selectedOptionsIds={selectedOptionsIds}
      />
    );
  };

  return (
    <div ref={optionGroupRef}>
      <StyledAlignItemsBetween>
        <StyledBulkTitleWrap>
          <Typography variant="headingExpressive16" testId="option-title">
            {isArabic ? optionGroup?.titleAr : optionGroup?.titleEn}
          </Typography>
          {toolTipText && <Tooltip info={toolTipText} placement={isArabic ? 'left' : 'right'} size="medium" />}
        </StyledBulkTitleWrap>
        {optionGroup.minQuantity > 0 && (
          <Typography
            variant="heading14"
            mt={16}
            color={showValidation && error ? theme.color.danger : theme.color.disabled}
            testId="required"
          >
            {t('required')}
          </Typography>
        )}
      </StyledAlignItemsBetween>
      {(!!optionGroup.minQuantity || !!optionGroup.maxQuantity || !!optionGroup.freeOptionsCount) && (
        <div>
          {getOptionsHintMessages(optionGroup.minQuantity, optionGroup.maxQuantity, optionGroup.freeOptionsCount).map(
            (msg, index, array) => (
              <Typography
                key={`${msg}-${optionGroup.uuid}`}
                variant="body14"
                mt={index === 0 ? 0 : 4}
                mb={index === array.length - 1 ? 16 : 4}
                color={showValidation && error ? theme.color.danger : theme.color.disabled}
                testId="option-hint-msg"
              >
                {msg}
              </Typography>
            ),
          )}
        </div>
      )}
      {renderOption()}
    </div>
  );
};

export default MenuItemOptionGroup;
